import React from 'react';
import {
	Box,
	Stack,
	SxProps,
	Tooltip,
	Typography,
	TypographyProps,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LoaderComponent from 'components/Loader';
import DateInfoTooltip, {
	DateInfoTooltipProps,
} from 'components/Tooltip/DateInfoTooltip';
import WidgetMoreActionMenu, {
	MoreOptionMenuProps,
} from 'components/Dropdown/WidgetMoreActionMenu';
import { ReactComponent as ToolTipIcon } from 'assets/PepWise2.0/InfoIcon.svg';

type InfoSummaryItem = {
	label: string | number;
	value: string | number | React.ReactNode;
	isLoading?: boolean;
	labelProps?: TypographyProps;
	valueProps?: TypographyProps;
	width?: string;
	height?: string;
	minWidth?: string;
	maxHeight?: string;
	maxWidth?: string;
	hasTooltip?: boolean;
};

type DataInfoSummaryCardProps = {
	infoCards: InfoSummaryItem[];
	infoTooltip?: Omit<DateInfoTooltipProps, 'children'>;
	infoOptionMenu?: MoreOptionMenuProps;
	isEditEnabled?: boolean;
	sx?: SxProps;
};

const InfoSummaryValue = ({
	value,
	isLoading,
	valueProps,
}: {
	value: string | number | React.ReactNode;
	isLoading?: boolean;
	valueProps?: TypographyProps;
}) => {
	if (isLoading) {
		return <LoaderComponent size={24} />;
	}

	if (React.isValidElement(value)) {
		return value;
	}

	return (
		<Typography
			variant="bodyValue"
			lineHeight="normal"
			sx={{
				color: '#333333',
				fontWeight: 500,
			}}
			{...valueProps}
		>
			{value}
		</Typography>
	);
};

const InfoSummaryItemCard = ({
	item,
}: {
	item: InfoSummaryItem;
}) => {
	const {
		label,
		value,
		labelProps,
		valueProps,
		isLoading,
		minWidth,
		width,
		height,
		maxHeight,
		maxWidth,
		hasTooltip,
	} = item;

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				flexGrow: 1,
				width: width || undefined,
				minWidth: width ? undefined : minWidth || 'auto',
				maxWidth: maxWidth || undefined,
				height: height || undefined,
				maxHeight: maxHeight || undefined,
			}}
		>
			<Stack spacing={0.5}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<Typography
						variant="bodyText"
						lineHeight="normal"
						sx={{
							color: '#828282',
							fontWeight: 500,
						}}
						{...labelProps}
					>
						{label}
					</Typography>

					{hasTooltip && (
						<Tooltip
							title="Cost is updated daily at 4:00 PM CST"
							arrow
						>
							<Box
								component="span"
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									ml: 0.5,
									cursor: 'help',
								}}
							>
								<ToolTipIcon
									height="18px"
									width="18px"
								/>
							</Box>
						</Tooltip>
					)}
				</Box>

				<InfoSummaryValue
					value={value}
					isLoading={isLoading}
					valueProps={valueProps}
				/>
			</Stack>
		</Box>
	);
};

const DataInfoSummaryCard = ({
	infoCards,
	infoTooltip,
	infoOptionMenu,
	isEditEnabled,
	sx,
}: DataInfoSummaryCardProps) => {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				width: '100%',
				minHeight: '100px',
				backgroundColor: '#FFFFFF',
				border: '1px solid #E0E0E0',
				borderRadius: '4px',
				boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
				px: 3,
				py: 2,
				boxSizing: 'border-box',
				...sx,
			}}
		>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: `repeat(${infoCards.length}, minmax(0, 1fr))`,
					alignItems: 'center',
					flexGrow: 1,
					gap: 3,
				}}
			>
				{infoCards.map((item, index) => (
					<InfoSummaryItemCard
						key={`${item.label}-${index}`}
						item={item}
					/>
				))}
			</Box>

			{(infoTooltip || (infoOptionMenu && isEditEnabled)) && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: 1,
						ml: 2,
						flexShrink: 0,
					}}
				>
					{!!infoTooltip && (
						<DateInfoTooltip {...infoTooltip}>
							<ToolTipIcon />
						</DateInfoTooltip>
					)}

					{!!infoOptionMenu && isEditEnabled && (
						<WidgetMoreActionMenu
							{...infoOptionMenu}
							icon={
								<MoreVertIcon
									sx={{
										color: '#39ACE7 !important',
									}}
								/>
							}
						/>
					)}
				</Box>
			)}
		</Box>
	);
};

export default DataInfoSummaryCard;